import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import Clock from 'react-live-clock';
import { useTranslation } from 'react-i18next';
import logo from '../assets/Navbar/logo1.png';
import backgroundImage from '../assets/Navbar/pexels-fwstudio-33348-172292.jpg';
import { FaRegEnvelope, FaWhatsapp, FaInstagram } from 'react-icons/fa';
import { LuPhone } from 'react-icons/lu';
import { SlLocationPin } from 'react-icons/sl';
import { IoLogoFacebook } from 'react-icons/io5';
import { GiHamburgerMenu } from 'react-icons/gi';
import { FaTiktok } from 'react-icons/fa';

// Define keyframes for slide-in animation
const slideIn = `
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const Navbar = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { t, i18n } = useTranslation('navbar'); // Specify the 'navbar' namespace


    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 0);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const handleLanguageChange = (event) => {
        const selectedLanguage = event.target.value;
        i18n.changeLanguage(selectedLanguage);
    };



    return (
        <div>
            {/* Inject keyframes for animation */}
            <style>{slideIn}</style>

            {/* Small Screen Section (below md, <768px) */}
            <div
                className="block md:hidden border-b-4 border-[#0D4E4A] relative"
                style={{
                    backgroundImage: ` url(${backgroundImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }}
            >
                {/* Centered Logo */}
                <div className="flex justify-center pt-4 pb-4">
                    <img
                        src={logo}
                        alt="Muthumuni Resort"
                        className="h-48 xs:h-32 sm:h-36 md:h-40"
                    />
                </div>

                {/* Book Online (top left) */}
                <div className="absolute top-4 left-4">
                    <a
                        href="/search"
                        className="inline-flex items-center justify-center bg-white/90 text-[#0D4E4A] text-xs font-semibold px-3 py-2 rounded-md shadow-md hover:bg-[#E4AB58] hover:text-[#1A2F2A] transition-all"
                    >
                        {t('bookOnline')}
                    </a>
                </div>

                {/* Clock + Location + Language (top right) */}
                <div className="absolute top-4 right-4 flex flex-col items-end gap-1.5">
                    <div className="bg-[#f8f8f8] px-3 py-1.5 rounded-md text-[#0D4E4A] text-sm font-medium shadow-sm">
                        <Clock format={'HH:mm:ss'} ticking={true} timezone={'Asia/Colombo'} />
                    </div>
                    <h3 className="text-xs sm:text-sm text-white font-medium">Maggona</h3>
                    <select
                        onChange={handleLanguageChange}
                        value={i18n.language}
                        className="
                            w-[50px]
                            border border-[#0D4E4A] 
                            rounded-md 
                            bg-white 
                            text-[#0D4E4A] 
                            text-base
                            px-1 py-1
                            cursor-pointer 
                            shadow-sm
                            focus:outline-none 
                            focus:ring-1 
                            focus:ring-[#0D4E4A]/60
                        "
                    >
                        <option value="en">🇬🇧</option>
                        <option value="de">🇩🇪</option>
                        <option value="ru">🇷🇺</option>
                        <option value="it">🇮🇹</option>
                        <option value="fr">🇫🇷</option>
                        <option value="zh">🇨🇳</option>
                        <option value="ja">🇯🇵</option>
                        <option value="cs">🇨🇿</option>
                        <option value="pl">🇵🇱</option>
                    </select>
                </div>

                {/* Hamburger Menu - Bottom Right */}
                <div className="absolute bottom-5 right-5">
                    <button
                        onClick={toggleMenu}
                        aria-label="Toggle mobile menu"
                        className="bg-transparent border-none p-1"
                    >
                        <GiHamburgerMenu size={32} color="white" className="drop-shadow-md" />
                    </button>
                </div>
            </div>

            {/* Medium Screen Section (md: 768px to 1024px) */}
            <div className="hidden md:block lg:hidden">
                {/* Top Section */}
                <div
                    className={`flex items-center justify-between text-black ${isScrolled ? 'shadow-sm' : ''}`}
                    style={{
                        backgroundImage: ` url(${backgroundImage})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        height: '120px',
                        borderBottom: '5px solid #0D4E4A',
                        padding: '0 12px',
                        position: 'relative',
                    }}
                >
                    <div className="absolute top-3 left-3">
                    </div>
                    {/* Centered Logo */}
                    <div
                        className="flex justify-center w-full"
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                        }}
                    >
                        <img src={logo} alt="Muthumuni Resort" style={{ height: '140px' }} />
                    </div>

                    {/* Social Icons and Clock in Top Right Corner */}
                    <div className="flex flex-col items-end gap-1" style={{ position: 'absolute', top: '12px', right: '12px' }}>
                        <div
                            className="bg-[white] px-2 py-1 rounded font-light text-[#0D4E4A] text-sm md:text-base"
                            style={{ width: 'auto', height: 'auto' }}
                        >
                            <Clock format={'HH:mm:ss'} ticking={true} timezone={'Asia/Colombo'} />
                        </div>
                        <h3 className="text-xs md:text-sm text-white">Maggona</h3>
                        <div className="flex gap-1 mt-1">
                            <a
                                href="tel:+94766111678"
                                className="text-white"
                                aria-label="Call us"
                                style={{
                                    transition: 'color 0.3s, transform 0.3s',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1)';
                                }}
                            >
                                <LuPhone size={16} color="#FFFFFF" />
                            </a>
                            <a
                                href="https://maps.app.goo.gl/338Jvrk1ux6z3RKPA"
                                className="text-white"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="View location"
                                style={{
                                    transition: 'color 0.3s, transform 0.3s',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1)';
                                }}
                            >
                                <SlLocationPin size={16} color="#FFFFFF" />
                            </a>
                            <a
                                href="mailto:mahibeach23@gmail.com"
                                className="text-white"
                                aria-label="Email us"
                                style={{
                                    transition: 'color 0.3s, transform 0.3s',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1)';
                                }}
                            >
                                <FaRegEnvelope size={16} color="#FFFFFF" />
                            </a>
                            <a
                                href="https://www.facebook.com/share/1BfGRTr59L/?mibextid=wwXIfr"
                                className="text-white"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Visit our Facebook page"
                                style={{
                                    transition: 'color 0.3s, transform 0.3s',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1)';
                                }}
                            >
                                <IoLogoFacebook size={16} color="#FFFFFF" />
                            </a>
                            <a
                                href="https://wa.me/94766111678"
                                className="text-white"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Contact us on WhatsApp"
                                style={{
                                    transition: 'color 0.3s, transform 0.3s',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1)';
                                }}
                            >
                                <FaWhatsapp size={16} color="#FFFFFF" />
                            </a>
                            <a
                                href="https://www.instagram.com/mahibeach_ayurveda?igsh=bjExYzI0NWY4azNu"
                                className="text-white"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Visit our Instagram page"
                                style={{
                                    transition: 'color 0.3s, transform 0.3s',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1)';
                                }}
                            >
                                <FaInstagram size={16} color="#FFFFFF" />
                            </a>
                            <a
                                href="https://www.tiktok.com/@.mahi.beach.ayurv?_r=1&_t=ZS-94BSC88Jb2S"
                                className="text-white"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Visit our TikTok page"
                                style={{
                                    transition: 'color 0.3s, transform 0.3s',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1)';
                                }}
                            >
                                <FaTiktok size={16} color="#FFFFFF" />
                            </a>
                        </div>
                        <div className="mt-2 w-full max-w-[160px] self-end">
                            <select
                                onChange={handleLanguageChange}
                                value={i18n.language}
                                className="
        w-full
        border border-[#0D4E4A] 
        rounded-[5px] 
        bg-white 
        text-[#0D4E4A] 
        text-xs md:text-sm
        px-2 py-1.5
        cursor-pointer 
        shadow-sm
        focus:outline-none 
        focus:ring-1 
        focus:ring-[#0D4E4A]/60
      "
                            >
                                <option value="en">🇬🇧 English</option>
                                <option value="de">🇩🇪 German</option>
                                <option value="ru">🇷🇺 Russian</option>
                                <option value="it">🇮🇹 Italiano</option>
                                <option value="fr">🇫🇷 Français</option>
                                <option value="zh">🇨🇳 中文</option>
                                <option value="ja">🇯🇵 日本語</option>
                                <option value="cs">🇨🇿 Čeština</option>
                                <option value="pl">🇵🇱 Polski</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Bottom Section (Medium Screen Navigation) */}
                <div
                    className="flex justify-between items-center px-2 py-2"
                    style={{
                        backgroundColor: '#0D4E4A',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1000,
                        boxShadow: isScrolled ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none',
                        overflowX: 'auto',
                    }}
                >
                    {/* Centered Navigation Links */}
                    <div className="flex justify-center flex-grow">
                        <ul className="flex mb-0 list-none text-sm md:text-base font-serif font-light whitespace-nowrap">
                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 py-2 block hover:text-[#E4AB58]"
                                    to="/home"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('home')}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 py-2 block hover:text-[#E4AB58]"
                                    to="/about-us"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('aboutUs')}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 py-2 block hover:text-[#E4AB58]"
                                    to="/rooms"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('rooms')}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 py-2 block hover:text-[#E4AB58]"
                                    to="/ayurveda"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('ayurveda')}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 py-2 block hover:text-[#E4AB58]"
                                    to="/yoga"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('yoga')}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 py-2 block hover:text-[#E4AB58]"
                                    to="/food"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('food')}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 py-2 block hover:text-[#E4AB58]"
                                    to="/events"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('events')}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 py-2 block hover:text-[#E4AB58]"
                                    to="/blogs"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('blogs')}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 py-2 block hover:text-[#E4AB58]"
                                    to="/tours"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('tours')}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 py-2 block hover:text-[#E4AB58]"
                                    to="/contact-us"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('contactUs')}
                                </NavLink>
                            </li>
                        </ul>
                    </div>
                    <div className="ml-auto">
                        <a
                            href="/search"
                            className="
        inline-flex items-center justify-center
        bg-[white]             
                hover:bg-[#E4AB58]        
                text-[#0D4E4A]
                font-bold text-sm md:text-base
        px-4 md:px-5
        py-2
        rounded-md
        shadow-md
        transition-all duration-300
        transform hover:scale-105 hover:shadow-lg
                focus:outline-none focus:ring-2 focus:ring-[#E4AB58]/50
      "
                        >
                            {t('bookOnline')}
                        </a>
                    </div>
                </div>
            </div>

            {/* Large Screen Section (lg and larger, ≥1024px) */}
            <div className="hidden lg:block">
                {/* Top Section */}
                <div
                    className={`flex items-center justify-between text-black ${isScrolled ? 'shadow-sm' : ''}`}
                    style={{
                        backgroundImage: ` url(${backgroundImage})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        height: '220px',
                        borderBottom: '5px solid #0D4E4A',
                        padding: '0 16px',
                        position: 'relative',
                    }}
                >
                    <div
                        className="absolute top-6 left-6 z-10"
                    >
                    </div>
                    {/* Centered Logo */}
                    <div
                        className="flex justify-center w-full mt-2"
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                        }}
                    >
                        <img src={logo} alt="Muthumuni Resort" style={{ height: '220px' }} />
                    </div>

                    {/* Social Icons and Clock in Top Right Corner */}
                    <div className="flex flex-col items-end gap-2" style={{ position: 'absolute', top: '30px', right: '16px' }}>
                        <div
                            className="bg-[white] px-2 py-1 rounded text-[#0D4E4A] text-lg md:text-xl font-light"
                            style={{ width: 'auto', height: '32px' }}
                        >
                            <Clock format={'HH:mm:ss'} ticking={true} timezone={'Asia/Colombo'} />
                        </div>
                        <h3 className="text-sm md:text-base text-white">Maggona</h3>
                        <div className="flex gap-1.5 mt-2">
                            <a
                                href="tel:+94766111678"
                                className="text-white"
                                aria-label="Call us"
                                style={{
                                    transition: 'color 0.3s, transform 0.3s',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1)';
                                }}
                            >
                                <LuPhone size={20} color="#FFFFFF" />
                            </a>
                            <a
                                href="https://maps.app.goo.gl/338Jvrk1ux6z3RKPA"
                                className="text-white"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="View location"
                                style={{
                                    transition: 'color 0.3s, transform 0.3s',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1)';
                                }}
                            >
                                <SlLocationPin size={20} color="#FFFFFF" />
                            </a>
                            <a
                                href="mailto:mahibeach23@gmail.com"
                                className="text-white"
                                aria-label="Email us"
                                style={{
                                    transition: 'color 0.3s, transform 0.3s',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1)';
                                }}
                            >
                                <FaRegEnvelope size={20} color="#FFFFFF" />
                            </a>
                            <a
                                href="https://www.facebook.com/share/1BfGRTr59L/?mibextid=wwXIfr"
                                className="text-white"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Visit our Facebook page"
                                style={{
                                    transition: 'color 0.3s, transform 0.3s',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1)';
                                }}
                            >
                                <IoLogoFacebook size={20} color="#FFFFFF" />
                            </a>
                            <a
                                href="https://wa.me/94766111678"
                                className="text-white"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Contact us on WhatsApp"
                                style={{
                                    transition: 'color 0.3s, transform 0.3s',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1)';
                                }}
                            >
                                <FaWhatsapp size={20} color="#FFFFFF" />
                            </a>
                            <a
                                href="https://www.instagram.com/mahibeach_ayurveda?igsh=bjExYzI0NWY4azNu"
                                className="text-white"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Visit our Instagram page"
                                style={{
                                    transition: 'color 0.3s, transform 0.3s',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1)';
                                }}
                            >
                                <FaInstagram size={20} color="#FFFFFF" />
                            </a>
                            <a
                                href="https://www.tiktok.com/@.mahi.beach.ayurv?_r=1&_t=ZS-94BSC88Jb2S"
                                className="text-white"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Visit our TikTok page"
                                style={{
                                    transition: 'color 0.3s, transform 0.3s',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1)';
                                }}
                            >
                                <FaTiktok size={20} color="#FFFFFF" />
                            </a>
                        </div>
                        <div className="mt-3 mr-1">
                            <select
                                onChange={handleLanguageChange}
                                value={i18n.language}
                                className="
        border border-[#0D4E4A] 
        rounded-[5px] 
        bg-white 
        text-[#0D4E4A] 
        text-sm md:text-base 
        px-3 py-1.5 
        cursor-pointer 
        min-w-[140px]
        shadow-sm
        focus:outline-none 
        focus:ring-1 
        focus:ring-[#0D4E4A]/60
      "
                            >
                                <option value="en">🇬🇧 English</option>
                                <option value="de">🇩🇪 German</option>
                                <option value="ru">🇷🇺 Russian</option>
                                <option value="it">🇮🇹 Italiano</option>
                                <option value="fr">🇫🇷 Français</option>
                                <option value="zh">🇨🇳 中文</option>
                                <option value="ja">🇯🇵 日本語</option>
                                <option value="cs">🇨🇿 Čeština</option>
                                <option value="pl">🇵🇱 Polski</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Bottom Section (Desktop Navigation) */}
                <div
                    className="flex justify-between items-center px-4 py-2 md:px-2 md:py-2 lg:px-6 xl:px-6"
                    style={{
                        backgroundColor: '#0D4E4A',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1000,
                        boxShadow: isScrolled ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none',
                    }}
                >
                    {/* Centered Navigation Links */}
                    <div className="flex justify-center flex-grow">
                        <ul className="flex mb-0 list-none text-lg md:text-xl lg:text-2xl xl:text-3xl font-serif font-light">
                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 md:px-3 py-2 block hover:text-[#E4AB58]"
                                    to="/home"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('home')}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 md:px-3 py-2 block hover:text-[#E4AB58]"
                                    to="/about-us"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('aboutUs')}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 md:px-3 py-2 block hover:text-[#E4AB58]"
                                    to="/rooms"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('rooms')}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 md:px-3 py-2 block hover:text-[#E4AB58]"
                                    to="/ayurveda"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('ayurveda')}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 md:px-3 py-2 block hover:text-[#E4AB58]"
                                    to="/yoga"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('yoga')}
                                </NavLink>
                            </li>

                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 md:px-3 py-2 block hover:text-[#E4AB58]"
                                    to="/food"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('food')}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 md:px-3 py-2 block hover:text-[#E4AB58]"
                                    to="/events"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('events')}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 md:px-3 py-2 block hover:text-[#E4AB58]"
                                    to="/blogs"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('blogs')}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 md:px-3 py-2 block hover:text-[#E4AB58]"
                                    to="/tours"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('tours')}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 md:px-3 py-2 block hover:text-[#E4AB58]"
                                    to="/contact-us"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('contactUs')}
                                </NavLink>
                            </li>
                        </ul>
                    </div>
                    <div className="ml-auto">
                        <a
                            href="/search"
                            className="
        inline-flex items-center justify-center
        bg-[white]             
        hover:bg-[#E4AB58]        
        text-[#0D4E4A]
        font-bold text-base md:text-lg lg:text-xl
        px-5 md:px-6 lg:px-8
        py-2.5 md:py-3
        rounded-md
        shadow-md
        transition-all duration-300
        transform hover:scale-105 hover:shadow-lg
                focus:outline-none focus:ring-2 focus:ring-[#E4AB58]/50
      "
                        >
                            {t('bookOnline')}
                        </a>
                    </div>
                </div>
            </div>

            {/* Mobile Menu */}
            {isMenuOpen && (
                <div
                    className="block md:hidden fixed top-0 right-0 h-screen w-4/5 max-w-[320px] 
               bg-white shadow-[-6px_0_20px_rgba(0,0,0,0.25)] 
               border-l border-gray-200 z-[1001] p-6 overflow-y-auto"
                    style={{ animation: 'slideIn 0.35s ease-out' }}
                >
                    {/* Close Button */}
                    <button
                        onClick={toggleMenu}
                        aria-label="Close menu"
                        className="absolute top-5 right-5 bg-[#0D4E4A] text-white rounded-full w-10 h-10 
                 flex items-center justify-center text-xl font-bold hover:bg-[#E4AB58] 
                 transition-all shadow-md"
                    >
                        ×
                    </button>

                    {/* Resort Selector */}
                    <div className="mt-16 mb-6">
                    </div>

                    {/* Language Selector */}
                    <div className="mb-8">
                        <label className="block text-sm text-gray-700 font-medium mb-2">
                            {t('language')}
                        </label>
                        <select
                            onChange={handleLanguageChange}
                            value={i18n.language}
                            className="
          w-full border border-gray-300 rounded-lg 
                    bg-white text-[#0D4E4A] px-4 py-2.5 
          text-base cursor-pointer focus:outline-none 
                    focus:ring-2 focus:ring-[#0D4E4A]/40 shadow-sm
        "
                        >
                            <option value="en">🇬🇧 English</option>
                            <option value="de">🇩🇪 German</option>
                            <option value="ru">🇷🇺 Russian</option>
                            <option value="it">🇮🇹 Italiano</option>
                            <option value="fr">🇫🇷 Français</option>
                            <option value="zh">🇨🇳 中文</option>
                            <option value="ja">🇯🇵 日本語</option>
                            <option value="cs">🇨🇿 Čeština</option>
                            <option value="pl">🇵🇱 Polski</option>
                        </select>
                    </div>

                    {/* Navigation Links */}
                    <ul className="flex flex-col mt-[30px]">
                        <li>
                            <NavLink
                                to="/home"
                                onClick={toggleMenu}
                                className="block text-black hover:bg-[#E4AB58] py-3 px-5 text-lg sm:text-[1.5rem] font-serif font-light"
                                style={({ isActive }) => ({
                                    textDecoration: isActive ? 'underline' : 'none',
                                    transition: 'color 0.3s, background-color 0.3s',
                                })}
                            >
                                {t('home')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/about-us"
                                onClick={toggleMenu}
                                className="block text-black hover:bg-[#E4AB58] py-3 px-5 text-lg sm:text-[1.5rem] font-serif font-light"
                                style={({ isActive }) => ({
                                    textDecoration: isActive ? 'underline' : 'none',
                                    transition: 'color 0.3s, background-color 0.3s',
                                })}
                            >
                                {t('aboutUs')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/rooms"
                                onClick={toggleMenu}
                                className="block text-black hover:bg-[#E4AB58] py-3 px-5 text-lg sm:text-[1.5rem] font-serif font-light"
                                style={({ isActive }) => ({
                                    textDecoration: isActive ? 'underline' : 'none',
                                    transition: 'color 0.3s, background-color 0.3s',
                                })}
                            >
                                {t('rooms')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/ayurveda"
                                onClick={toggleMenu}
                                className="block text-black hover:bg-[#E4AB58] py-3 px-5 text-lg sm:text-[1.5rem] font-serif font-light"
                                style={({ isActive }) => ({
                                    textDecoration: isActive ? 'underline' : 'none',
                                    transition: 'color 0.3s, background-color 0.3s',
                                })}
                            >
                                {t('ayurveda')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/yoga"
                                onClick={toggleMenu}
                                className="block text-black hover:bg-[#E4AB58] py-3 px-5 text-lg sm:text-[1.5rem] font-serif font-light"
                                style={({ isActive }) => ({
                                    textDecoration: isActive ? 'underline' : 'none',
                                    transition: 'color 0.3s, background-color 0.3s',
                                })}
                            >
                                {t('yoga')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/food"
                                onClick={toggleMenu}
                                className="block text-black hover:bg-[#E4AB58] py-3 px-5 text-lg sm:text-[1.5rem] font-serif font-light"
                                style={({ isActive }) => ({
                                    textDecoration: isActive ? 'underline' : 'none',
                                    transition: 'color 0.3s, background-color 0.3s',
                                })}
                            >
                                {t('food')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/events"
                                onClick={toggleMenu}
                                className="block text-black hover:bg-[#E4AB58] py-3 px-5 text-lg sm:text-[1.5rem] font-serif font-light"
                                style={({ isActive }) => ({
                                    textDecoration: isActive ? 'underline' : 'none',
                                    transition: 'color 0.3s, background-color 0.3s',
                                })}
                            >
                                {t('events')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/blogs"
                                onClick={toggleMenu}
                                className="block text-black hover:bg-[#E4AB58] py-3 px-5 text-lg sm:text-[1.5rem] font-serif font-light"
                                style={({ isActive }) => ({
                                    textDecoration: isActive ? 'underline' : 'none',
                                    transition: 'color 0.3s, background-color 0.3s',
                                })}
                            >
                                {t('blogs')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/tours"
                                onClick={toggleMenu}
                                className="block text-black hover:bg-[#E4AB58] py-3 px-5 text-lg sm:text-[1.5rem] font-serif font-light"
                                style={({ isActive }) => ({
                                    textDecoration: isActive ? 'underline' : 'none',
                                    transition: 'color 0.3s, background-color 0.3s',
                                })}
                            >
                                {t('tours')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/contact-us"
                                onClick={toggleMenu}
                                className="block text-black hover:bg-[#E4AB58] py-3 px-5 text-lg sm:text-[1.5rem] font-serif font-light"
                                style={({ isActive }) => ({
                                    textDecoration: isActive ? 'underline' : 'none',
                                    transition: 'color 0.3s, background-color 0.3s',
                                })}
                            >
                                {t('contactUs')}
                            </NavLink>
                        </li>
                    </ul>

                    {/* Social Icons */}
                    <div className="flex justify-center gap-3 mt-4 pt-4 border-t border-gray-200">
                        <a
                            href="tel:+94766111678"
                            aria-label="Call us"
                            className="text-[#0D4E4A]"
                            style={{
                                transition: 'color 0.3s, transform 0.3s',
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.color = '#E4AB58';
                                e.target.style.transform = 'scale(1.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.color = '#0D4E4A';
                                e.target.style.transform = 'scale(1)';
                            }}
                        >
                            <LuPhone size={20} className="sm:size-24" color="#0D4E4A" />
                        </a>
                        <a
                            href="https://maps.app.goo.gl/338Jvrk1ux6z3RKPA"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="View location"
                            className="text-[#0D4E4A]"
                            style={{
                                transition: 'color 0.3s, transform 0.3s',
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.color = '#E4AB58';
                                e.target.style.transform = 'scale(1.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.color = '#0D4E4A';
                                e.target.style.transform = 'scale(1)';
                            }}
                        >
                            <SlLocationPin size={20} className="sm:size-24" color="#0D4E4A" />
                        </a>
                        <a
                            href="mailto:mahibeach23@gmail.com"
                            aria-label="Email us"
                            className="text-[#0D4E4A]"
                            style={{
                                transition: 'color 0.3s, transform 0.3s',
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.color = '#E4AB58';
                                e.target.style.transform = 'scale(1.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.color = '#0D4E4A';
                                e.target.style.transform = 'scale(1)';
                            }}
                        >
                            <FaRegEnvelope size={20} className="sm:size-24" color="#0D4E4A" />
                        </a>
                        <a
                            href="https://www.facebook.com/share/1BfGRTr59L/?mibextid=wwXIfr"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Visit our Facebook page"
                            className="text-[#0D4E4A]"
                            style={{
                                transition: 'color 0.3s, transform 0.3s',
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.color = '#E4AB58';
                                e.target.style.transform = 'scale(1.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.color = '#0D4E4A';
                                e.target.style.transform = 'scale(1)';
                            }}
                        >
                            <IoLogoFacebook size={20} className="sm:size-24" color="#0D4E4A" />
                        </a>
                        <a
                            href="https://wa.me/94766111678"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Contact us on WhatsApp"
                            className="text-[#0D4E4A]"
                            style={{
                                transition: 'color 0.3s, transform 0.3s',
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.color = '#E4AB58';
                                e.target.style.transform = 'scale(1.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.color = '#0D4E4A';
                                e.target.style.transform = 'scale(1)';
                            }}
                        >
                            <FaWhatsapp size={20} className="sm:size-24" color="#0D4E4A" />
                        </a>
                        <a
                            href="https://www.instagram.com/mahibeach_ayurveda?igsh=bjExYzI0NWY4azNu"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Visit our Instagram page"
                            className="text-[#0D4E4A]"
                            style={{
                                transition: 'color 0.3s, transform 0.3s',
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.color = '#E4AB58';
                                e.target.style.transform = 'scale(1.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.color = '#0D4E4A';
                                e.target.style.transform = 'scale(1)';
                            }}
                        >
                            <FaInstagram size={20} className="sm:size-24" color="#0D4E4A" />
                        </a>
                        <a
                            href="https://www.tiktok.com/@.mahi.beach.ayurv?_r=1&_t=ZS-94BSC88Jb2S"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Visit our TikTok page"
                            className="text-[#0D4E4A]"
                            style={{
                                transition: 'color 0.3s, transform 0.3s',
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.color = '#E4AB58';
                                e.target.style.transform = 'scale(1.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.color = '#0D4E4A';
                                e.target.style.transform = 'scale(1)';
                            }}
                        >
                            <FaTiktok size={20} className="sm:size-24" color="#0D4E4A" />
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Navbar;